import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FileText, Loader2, X } from 'lucide-react';

/**
 * «Mandar plantilla» (1/11/2026). Fuera de la ventana de 24 h WhatsApp solo
 * deja escribir con una plantilla APROBADA. Aquí se listan las de la cuenta
 * (se leen del proveedor al abrir), se rellenan sus variables con una vista
 * previa exacta y se manda; queda firmada con quien la manda. Nunca sale
 * sola. Si no hay plantillas o no se pueden leer, se dice en claro.
 */

/** El texto de la plantilla con lo que se ha escrito en cada variable. */
export function rellenar(texto, valores) {
  return (texto || '').replace(/\{\{\s*([A-Za-z0-9_]{1,60})\s*\}\}/g,
    (todo, clave) => (valores[clave] ? valores[clave] : todo));
}

function clavesUnicas(plantilla) {
  const vistas = [];
  (plantilla?.variables || []).forEach((v) => { if (!vistas.includes(v.clave)) vistas.push(v.clave); });
  return vistas;
}

export default function PlantillasWhatsApp({ tel, onEnviada, onCerrar }) {
  const [estado, setEstado] = useState('cargando'); // cargando | ok | error
  const [fallo, setFallo] = useState('');
  const [lista, setLista] = useState([]);
  const [elegida, setElegida] = useState(null);
  const [valores, setValores] = useState({});
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/api/whatsapp/plantillas')
      .then((r) => {
        if (!vivo) return;
        setLista(Array.isArray(r.data?.plantillas) ? r.data.plantillas : []);
        setEstado('ok');
      })
      .catch((e) => {
        if (!vivo) return;
        setFallo(mensajeError(e, 'No se han podido leer las plantillas'));
        setEstado('error');
      });
    return () => { vivo = false; };
  }, []);

  const claves = useMemo(() => clavesUnicas(elegida), [elegida]);
  const completa = claves.every((c) => (valores[c] || '').trim());
  const previa = elegida
    ? [rellenar(elegida.cabecera, valores), rellenar(elegida.cuerpo, valores), elegida.pie || '']
      .filter((p) => p.trim()).join('\n')
    : '';

  const elegir = (p) => {
    if (!p.se_puede) return;
    setElegida(p);
    setValores({});
  };

  const enviar = async () => {
    if (!elegida || !completa || enviando) return;
    setEnviando(true);
    try {
      await api.post(`/api/whatsapp/conversaciones/${tel}/plantilla`, {
        nombre: elegida.nombre, idioma: elegida.idioma, variables: valores,
      });
      toast.success('Plantilla enviada');
      onEnviada();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido mandar la plantilla'));
    } finally {
      setEnviando(false);
    }
  };

  const aprobadas = lista.filter((p) => p.se_puede);
  return (
    <div className="border rounded-lg bg-background p-3 space-y-2" data-testid="wa-plantillas">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm flex items-center gap-1.5">
          <FileText className="w-4 h-4" /> Mandar una plantilla aprobada
        </div>
        <Button type="button" size="icon" variant="ghost" onClick={onCerrar}
          aria-label="Cerrar las plantillas" data-testid="wa-plantillas-cerrar">
          <X className="w-4 h-4" />
        </Button>
      </div>
      {estado === 'cargando' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Leyendo las plantillas de la cuenta…
        </div>
      )}
      {estado === 'error' && (
        <div className="text-sm text-red-700" data-testid="wa-plantillas-fallo">{fallo}</div>
      )}
      {estado === 'ok' && aprobadas.length === 0 && (
        <div className="text-sm text-muted-foreground" data-testid="wa-plantillas-vacia">
          No hay ninguna plantilla aprobada en la cuenta de WhatsApp que se pueda mandar desde aquí.
          Se crean y se aprueban en Meta, que tarda entre 24 y 48 h.
        </div>
      )}
      {estado === 'ok' && lista.length > 0 && !elegida && (
        <ul className="max-h-48 overflow-y-auto space-y-1" data-testid="wa-plantillas-lista">
          {lista.map((p) => (
            <li key={`${p.nombre}-${p.idioma}`}>
              <button type="button" onClick={() => elegir(p)} disabled={!p.se_puede}
                data-testid="wa-plantilla"
                className={`w-full text-left rounded-md px-2 py-1.5 text-sm ${
                  p.se_puede ? 'hover:bg-muted/60' : 'opacity-60 cursor-not-allowed'}`}>
                <span className="font-medium">{p.nombre}</span>
                <span className="text-xs text-muted-foreground"> · {p.idioma}{p.categoria ? ` · ${p.categoria}` : ''}</span>
                <span className="block text-xs text-muted-foreground truncate">
                  {p.se_puede ? p.cuerpo : p.motivo}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {elegida && (
        <div className="space-y-2" data-testid="wa-plantilla-elegida">
          <div className="text-sm">
            <span className="font-medium">{elegida.nombre}</span>
            <span className="text-xs text-muted-foreground"> · {elegida.idioma}</span>
            <Button type="button" variant="link" size="sm" className="h-auto px-2"
              onClick={() => setElegida(null)}>Elegir otra</Button>
          </div>
          {claves.map((clave) => (
            <label key={clave} className="flex items-center gap-2 text-sm">
              <span className="w-24 shrink-0 text-muted-foreground">{`{{${clave}}}`}</span>
              <Input value={valores[clave] || ''} aria-label={`Variable ${clave}`}
                onChange={(e) => setValores((v) => ({ ...v, [clave]: e.target.value }))}
                data-testid={`wa-variable-${clave}`} className="h-8" />
            </label>
          ))}
          <div className="rounded-md bg-[#D9FDD3] text-[#111B21] px-2.5 py-1.5 text-sm whitespace-pre-wrap"
            data-testid="wa-plantilla-previa">{previa}</div>
          <div className="flex justify-end">
            <Button type="button" onClick={enviar} disabled={!completa || enviando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="wa-plantilla-enviar">
              {enviando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Enviar plantilla
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
